import { useTranslation } from 'react-i18next';

/** Unit labels, taken from the `common:units` strings. */
export type UnitLabels = { bytes: string; kb: string; mb: string; gb: string; tb: string };

const STEP = 1024;
const UNIT_KEYS = ['bytes', 'kb', 'mb', 'gb', 'tb'] as const;

/**
 * Binary byte size (1 KB = 1024 B) with one decimal, for example `48.0 MB`. Plain bytes stay whole
 * numbers. Negative and non-finite values count as zero.
 */
export function formatBytes(bytes: number, labels: UnitLabels, locale?: string): string {
  let value = Number.isFinite(bytes) && bytes > 0 ? bytes : 0;
  let unit = 0;
  while (value >= STEP && unit < UNIT_KEYS.length - 1) {
    value /= STEP;
    unit += 1;
  }
  // 1023.96 KB would print as "1024.0 KB": that is "1.0 MB".
  if (unit > 0 && unit < UNIT_KEYS.length - 1 && Math.round(value * 10) / 10 >= STEP) {
    value /= STEP;
    unit += 1;
  }
  const label = labels[UNIT_KEYS[unit] ?? 'bytes'];
  if (unit === 0) return `${Math.round(value)} ${label}`;
  const number = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
    useGrouping: false,
  }).format(value);
  return `${number} ${label}`;
}

/** `formatBytes` bound to the current UI language and the translated unit labels. */
export function useFormatBytes(): (bytes: number) => string {
  const { t, i18n } = useTranslation('common');
  const labels: UnitLabels = {
    bytes: t('units.bytes'),
    kb: t('units.kb'),
    mb: t('units.mb'),
    gb: t('units.gb'),
    tb: t('units.tb'),
  };
  return (bytes) => formatBytes(bytes, labels, i18n.language);
}
